import React from 'react'
import { withStyles } from '@mui/styles'
import { FormControl, Select, MenuItem, ListItemText, Icon } from '@mui/material'

import { Tooltip, Checkbox, Typography, Box } from '@mui/material'

import { selectInputStyles } from './select-input.styles'

/**
 * @typedef {object} SelectInputOption
 * @property {string | number} key - El valor único de la opción.
 * @property {string} value - El texto visible de la opción.
 * @property {boolean} [disabled] - Si es true, la opción está deshabilitada.
 */

/**
 * @typedef {object} SelectInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string | string[]} target.value - El valor seleccionado (o array de valores si multiple es true).
 */

/**
 * @typedef {object} SelectInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {SelectInputOption[]} [values=[]] - Array de opciones disponibles para seleccionar.
 * @property {boolean} [multiple=false] - Si es true, permite seleccionar múltiples opciones.
 * @property {boolean} [readOnly=false] - Si es true, el campo se muestra como texto de solo lectura.
 * @property {boolean} [selectAll=false] - Si es true, muestra una opción para seleccionar todas las opciones.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {boolean} [disabled=false] - Si es true, el campo está deshabilitado.
 * @property {string | string[]} [value] - El valor actual seleccionado (string para simple, array para multiple).
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} [tooltip] - Texto informativo que se muestra en un icono de ayuda junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {function(SelectInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia la selección.
 */

/**
 * Un componente select (lista desplegable) que soporta selección simple o múltiple.
 *
 * Permite seleccionar una o varias opciones de una lista, con soporte para checkboxes
 * en modo múltiple, opción "Seleccionar todo", y visualización de selecciones múltiples
 * como texto separado por comas. Incluye modo de solo lectura y tooltips informativos.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [pais, setPais] = useState('');
 *
 *   const paises = [
 *     { key: 'es', value: 'España' },
 *     { key: 'fr', value: 'Francia' },
 *     { key: 'it', value: 'Italia' }
 *   ];
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 *     setPais(e.target.value);
 *   };
 *
 *   return (
 *     <SelectInput
 *       title="País"
 *       name="pais"
 *       value={pais}
 *       values={paises}
 *       onChange={handleChange}
 *       required
 *       tooltip="Seleccione su país"
 *     />
 *   );
 * };
 *
 * @param {SelectInputProps} props - Las props del componente SelectInput.
 */
export const SelectInput = React.memo(
  withStyles(selectInputStyles)(
    ({ classes, values = [], multiple = false, readOnly = false, selectAll = false, ...props }) => {
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      const value = props.value || (multiple ? [] : '')
      const getValue = () => {
        if (multiple) {
          let optionsSelected = []
          value.forEach((v) => {
            const optionSelected = values.find((item) => item.key === v)
            if (optionSelected) optionsSelected.push(optionSelected.value)
          })
          return optionsSelected.join(', ')
        } else {
          let optionSelected
          let valueSelected = '--'
          if (value && values) {
            optionSelected = values.find((option) => option.key === value)
            valueSelected = optionSelected ? optionSelected.value : '--'
          }
          return valueSelected
        }
      }

      const selectValues = () => {
        return values.map((item, i) => (
          <MenuItem key={i} value={item.key} disabled={item.disabled}>
            {multiple ? (
              <>
                <Checkbox checked={value.indexOf(item.key) > -1} />
                <ListItemText primary={item.value} />
              </>
            ) : (
              item.value
            )}
          </MenuItem>
        ))
      }

      const multipleProps =
        multiple && values.length
          ? {
              renderValue: (selected) =>
                selected
                  .map((item) => {
                    let found = values.find((value) => value.key === item)
                    return found ? found.value : 'Unknown'
                  })
                  .join(', '),
            }
          : {}

      const allSelected =
        multiple && values.length && value.length ? values.length === value.length : false

      const changeHandle = (e) => {
        if (selectAll && e.target.value.includes('all')) {
          return props.onChange({
            ...e,
            target: {
              ...e.target,
              value: allSelected ? [] : values.map((el) => el.key),
            },
          })
        }
        return props.onChange(e)
      }

      return (
        <FormControl
          variant="outlined"
          className={classes.root}
          required={props.required}
          disabled={props.disabled}
        >
          <Box display="flex" flexDirection="horizontal">
            {props.title && (
              <Typography variant={`subtitle${typographyVariant}`}>
                {props.title} {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
              </Typography>
            )}
            {props.tooltip && (
              <span style={{ fontSize: 16, marginLeft: 4 }}>
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                      {props.tooltip}
                    </Typography>
                  }
                >
                  <Icon fontSize="inherit">help_outline</Icon>
                </Tooltip>
              </span>
            )}
          </Box>
          {readOnly ? (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          ) : (
            <Select
              autoWidth
              className={classes.input}
              name={props.name}
              value={value}
              multiple={multiple}
              onChange={changeHandle}
              MenuProps={{
                variant: 'menu',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                sx: {
                  '& .MuiPaper-root': {
                    transformOrigin: 'left top !important',
                  },
                  '& .MuiList-root': {
                    textAlign: 'left',
                  },
                },
              }}
              {...multipleProps}
            >
              {selectAll && multiple ? (
                <MenuItem divider value="all">
                  <Checkbox checked={allSelected ? true : false}></Checkbox>
                  <ListItemText>Seleccionar todos</ListItemText>
                </MenuItem>
              ) : null}
              {selectValues()}
            </Select>
          )}
        </FormControl>
      )
    }
  )
)
